import { useState, useEffect, useMemo } from "react";

import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// Import i18n
import "./i18n";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";

import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

import theme from "assets/theme";

import themeDark from "assets/theme-dark";

import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";

import routes, { getRoutes } from "routes";
import { HomeRedirect, SignOut } from "components/AuthRedirects";

import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { useTranslation } from "react-i18next";

import brandWhite from "assets/images/logo.png";
import brandDark from "assets/images/logo.png";

export default function App() {
  const { t, ready } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  useEffect(() => {
    document.title = "Haram Store";
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const renderRoutes = (allRoutes) => {
    if (!Array.isArray(allRoutes)) {
      console.error("allRoutes is not an array:", allRoutes);
      return null;
    }
    return allRoutes.map((route) => {
      if (route.collapse) {
        return renderRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });
  };

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right={direction === "rtl" ? "auto" : "2rem"}
      left={direction === "rtl" ? "2rem" : "auto"}
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  // Show loading state while translations are being loaded
  if (!ready) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontSize: "18px",
          }}
        >
          Loading...
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {direction === "rtl" && rtlCache && (
        <style>
          {`
            * {
              direction: rtl;
            }
            body {
              direction: rtl;
              text-align: right;
            }
            .MuiTypography-root {
              direction: rtl;
              text-align: right;
            }
            .MuiTableCell-root {
              text-align: right;
            }
            .MuiTableHead-root .MuiTableCell-root {
              text-align: right;
            }
            .MuiDrawer-paper {
              direction: rtl;
            }
            .MuiListItem-root {
              direction: rtl;
            }
            .MuiListItemText-root {
              text-align: right;
            }
            .MuiListItemIcon-root {
              margin-left: 16px;
              margin-right: 0;
            }
            .MuiButton-root {
              direction: rtl;
            }
            .MuiInputBase-root {
              direction: rtl;
            }
            .MuiFormControl-root {
              direction: rtl;
            }
            .MuiSelect-select {
              text-align: right;
            }
            .MuiMenuItem-root {
              text-align: right;
            }
            .MuiDialogTitle-root {
              text-align: right;
            }
            .MuiDialogContent-root {
              text-align: right;
            }
            .MuiDialogActions-root {
              flex-direction: row-reverse;
            }
            .MuiTable-root {
              direction: rtl;
            }
            .MuiTableCell-root {
              text-align: right;
            }
            .MuiTableHead-root .MuiTableCell-root {
              text-align: right;
            }
          `}
        </style>
      )}
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="متجر الحرم"
            routes={ready ? getRoutes(t) : routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          {/* The Material-Dashboard template "Configurator" (theme demo panel)
              is a development toy — keep it out of production builds. */}
          {process.env.NODE_ENV !== "production" && (
            <>
              <Configurator />
              {configsButton}
            </>
          )}
        </>
      )}
      {layout === "vr" && process.env.NODE_ENV !== "production" && <Configurator />}
      <Routes>
        {renderRoutes(ready ? getRoutes(t) : routes)}
        {/* Bare domain and unknown URLs: keep the session, route by auth state.
            Sending these to the sign-in form made logged-in admins believe
            their session was lost whenever they opened a new tab. */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/authentication/sign-out" element={<SignOut />} />
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </ThemeProvider>
  );
}
